import React, { useState, useEffect } from 'react';
import { useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useGetHistoryQuery } from '../redux/services/historyApi';
import { useToast } from '../context/ToastContext';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Link,
} from '@mui/material';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Local storage key for filters
const STORAGE_KEY = 'historyFilters';

const getSavedFilters = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
      page: 1,
      limit: 10,
      status: 'S',
    };
  } catch (error) {
    return {
      page: 1,
      limit: 10,
      status: 'S',
    };
  }
};

const History = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  
  // Initialize filters from URL params and localStorage
  const getInitialFilters = () => {
    const savedFilters = getSavedFilters();
    const urlPage = searchParams.get('page');
    
    return {
      ...savedFilters,
      page: urlPage ? parseInt(urlPage, 10) : savedFilters.page,
    };
  };
  
  const [filters, setFilters] = useState(getInitialFilters());
  const [expandedMessages, setExpandedMessages] = useState(new Set());

  const { data: historyData, isLoading, error } = useGetHistoryQuery(filters);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  // Update URL when page changes
  useEffect(() => {
    const newSearchParams = new URLSearchParams();
    if (filters.page > 1) {
      newSearchParams.set('page', filters.page.toString());
    }
    
    const newSearch = newSearchParams.toString();
    const currentSearch = searchParams.toString();
    
    if (newSearch !== currentSearch) {
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [filters.page, setSearchParams]);

  // Handle URL changes (back/forward navigation, bookmarks)
  useEffect(() => {
    const urlPage = searchParams.get('page');
    const currentPage = urlPage ? parseInt(urlPage, 10) : 1;
    
    if (currentPage !== filters.page && currentPage > 0) {
      setFilters(prev => ({ ...prev, page: currentPage }));
    }
  }, [searchParams]);

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      // Reset to page 1 when changing non-page filters
      ...(name !== 'page' ? { page: 1 } : {}),
    }));
    
    // Clear expanded messages when changing page or filters
    if (name === 'page' || name === 'limit' || name === 'status') {
      setExpandedMessages(new Set());
    }
  };

  const handlePreviousPage = () => {
    if (filters.page > 1) {
      handleFilterChange('page', filters.page - 1);
    }
  };

  const handleNextPage = () => {
    // Only allow next page if we have data and it's not empty
    if (historyData?.data && historyData.data.length > 0) {
      handleFilterChange('page', filters.page + 1);
    }
  };

  const formatDateTime = (dateTimeString) => {
    // Date is already formatted as DD/MM/YYYY HH24:MI from backend
    return dateTimeString || '-';
  };

  const toggleMessageExpansion = (historyId) => {
    setExpandedMessages(prev => {
      const newSet = new Set(prev);
      const queryKey = `query-${historyId}`;
      const respKey = `resp-${historyId}`;
      
      if (newSet.has(queryKey) || newSet.has(respKey)) {
        // If any message is expanded, collapse both
        newSet.delete(queryKey);
        newSet.delete(respKey);
      } else {
        // If none are expanded, expand both
        newSet.add(queryKey);
        newSet.add(respKey);
      }
      return newSet;
    });
  };

  const ExpandableText = ({ text, messageKey, historyId, maxLength = 100 }) => {
    if (!text) return '-';
    
    const isExpanded = expandedMessages.has(messageKey);
    const needsTruncation = text.length > maxLength;
    
    if (!needsTruncation) {
      return text;
    }
    
    return (
      <Box>
        {isExpanded ? text : `${text.substring(0, maxLength)}`}
        {needsTruncation && (
          <Link
            component="button"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              toggleMessageExpansion(historyId);
            }}
            sx={{ 
              ml: 0.5, 
              cursor: 'pointer',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' }
            }}
          >
            {isExpanded ? ' [collapse]' : '...'}
          </Link>
        )}
      </Box>
    );
  };

  if (error) {
    console.error('Error loading history:', error);
    showToast(error.data?.message || 'Error loading history', 'error');
  }

  const hasData = historyData?.data && historyData.data.length > 0;
  const canGoNext = hasData && historyData.data.length === filters.limit;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        History
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="S">Approved</MenuItem>
                <MenuItem value="R">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Client Name</TableCell>
                    <TableCell>Query Message</TableCell>
                    <TableCell>Response Message</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hasData ? (
                    historyData.data.map((historyItem) => (
                      <TableRow key={historyItem.id}>
                        <TableCell>{historyItem.id}</TableCell>
                        <TableCell>
                          {historyItem.client_name && historyItem.client_id ? (
                            <Link
                              component={RouterLink}
                              to={`/clients/${historyItem.client_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                            >
                              {historyItem.client_name}
                            </Link>
                          ) : (
                            historyItem.client_name || '-'
                          )}
                        </TableCell>
                        <TableCell 
                          sx={{ maxWidth: 300, wordWrap: 'break-word' }}
                        >
                          <ExpandableText 
                            text={historyItem.query_msg} 
                            messageKey={`query-${historyItem.id}`}
                            historyId={historyItem.id}
                            maxLength={100}
                          />
                        </TableCell>
                        <TableCell 
                          sx={{ maxWidth: 300, wordWrap: 'break-word' }}
                        >
                          <ExpandableText 
                            text={historyItem.resp_msg} 
                            messageKey={`resp-${historyItem.id}`}
                            historyId={historyItem.id}
                            maxLength={100}
                          />
                        </TableCell>
                        <TableCell>{formatDateTime(historyItem.created)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No history records found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Pagination and Records per page */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mt: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Records per page</InputLabel>
              <Select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                label="Records per page"
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </Select>
            </FormControl>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handlePreviousPage}
                disabled={filters.page <= 1}
                startIcon={<NavigateBeforeIcon />}
              >
                Previous
              </Button>
              <Typography 
                variant="body1" 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  px: 2,
                  fontWeight: 'medium'
                }}
              >
                Page {filters.page}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleNextPage}
                disabled={!canGoNext}
                endIcon={<NavigateNextIcon />}
              >
                Next
              </Button>
            </Box>
          </Box>

          {hasData && (
            <Typography 
              variant="body2" 
              color="textSecondary" 
              align="center" 
              sx={{ mt: 2 }}
            >
              Showing {historyData.data.length} records on page {filters.page}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default History; 